import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { TableFormModal } from '@/components/menu/TableFormModal';
import { cacheTables, getCachedTables } from '@/lib/db';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import { queryClient } from '@/lib/queryClient';
import { usePagination } from '@smarty/shared';
import { useDebounce } from '@smarty/shared';
import {
  Page,
  Card,
  Button,
  Badge,
  Modal,
  BlockStack,
  InlineStack,
  InlineGrid,
  Box,
  Text,
  EmptyState,
  ButtonGroup,
  Banner,
  ChoiceList,
  IndexFiltersMode,
  useSetIndexFiltersMode,
  type SortButtonChoice,
} from '@/lib/polaris';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { StatsSnapshotBar } from '@/components/admin/StatsSnapshotBar';
import {
  PointOfSaleIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  RefreshIcon,
} from '@/lib/polaris-icons';
import { QRCodeSVG } from 'qrcode.react';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { ROUTES } from '@/config/routes.config';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { ListView, buildListViewPagination } from '@/components/admin/ListView';
import { getTablesListConfig, type TableListItem } from '@/config/listViews';
import { useListViews } from '@/hooks/useListViews';
import { useStandardListFilters } from '@/hooks/useStandardListFilters';
import { devLog } from '@/lib/utils';

// `type` (no `interface`): un type-alias de objeto es asignable a tipos con index
// signature como `CachedRecord` (requerido por `cacheTables`); una interface no lo es.
type Table = {
  id: string;
  table_number: string;
  name?: string | null;
  capacity: number;
  status: 'free' | 'occupied' | 'reserved' | 'maintenance';
  location?: string | null;
  notes?: string | null;
  branch_id: string;
  qr_code?: string | null;
  qr_id?: string | null;
};

const statusLabels = {
  free: 'Libre',
  occupied: 'Ocupada',
  reserved: 'Reservada',
  maintenance: 'Mantenimiento',
};

// Tonos alineados con TableListItem['status_tone'] (paleta del ListView de mesas).
const statusTones: Record<Table['status'], 'success' | 'critical' | 'attention' | 'subdued'> = {
  free: 'success',
  occupied: 'critical',
  reserved: 'attention',
  maintenance: 'subdued',
};

const LIST_ID = 'tables';
const TABLES_SYSTEM_VIEWS = [
  { id: 'todos', label: 'Todos', isSystem: true, query: '', filterValues: { status: [] as string[] } },
  { id: 'libres', label: 'Libres', isSystem: true, query: '', filterValues: { status: ['free'] } },
  { id: 'ocupadas', label: 'Ocupadas', isSystem: true, query: '', filterValues: { status: ['occupied'] } },
  { id: 'reservadas', label: 'Reservadas', isSystem: true, query: '', filterValues: { status: ['reserved'] } },
  { id: 'mantenimiento', label: 'Mantenimiento', isSystem: true, query: '', filterValues: { status: ['maintenance'] } },
];

const TABLES_SORT_OPTIONS: SortButtonChoice[] = [
  { value: 'table_number asc', label: 'Mesa', directionLabel: 'Número (1-9)' },
  { value: 'table_number desc', label: 'Mesa', directionLabel: 'Número (9-1)' },
];

/** Detecta AbortError nativo o el objeto que devuelve Supabase al abortar (message/details con "abort"). */
function isAbortError(e: unknown): boolean {
  if (e instanceof Error) return e.name === 'AbortError' || /aborted/i.test(e.message ?? '');
  if (typeof e === 'object' && e !== null) {
    const o = e as { name?: string; message?: string; details?: string };
    if (o.name === 'AbortError') return true;
    const msg = [o.message, o.details].filter(Boolean).join(' ');
    if (msg && /aborted?|signal\s+is\s+aborted/i.test(msg)) return true;
  }
  return false;
}

/** Fila de tables desde Supabase (sin qr_code/qr_id). */
type TableRow = Pick<Table, 'id' | 'table_number' | 'name' | 'capacity' | 'status' | 'location' | 'notes' | 'branch_id'>;
/** Fila de table_qr_codes. */
interface QrCodeRow {
  id: string;
  qr_code: string;
  table_number: string;
  is_active?: boolean;
}

/**
 * Lo que trae la consulta: la página de mesas y cuántas hay en total.
 *
 * `total` es `null` cuando las filas salieron del disco (offline o fallo): ahí no hay conteo del
 * servidor, y **inventar uno rompería el paginador** — mostraría páginas que no existen.
 */
interface ListadoMesas {
  filas: Table[];
  total: number | null;
}

/** 🪤 Referencia estable: un `?? []` nuevo por render invalidaría los `useMemo` de stats y filas. */
const SIN_MESAS: Table[] = Object.freeze([]) as Table[];

export default function TablesPage() {
  const navigate = useNavigate();
  const { branchId, tenantId, loading: authLoading } = useAuth();
  const toast = useAdminToast();
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableFormOpen, setTableFormOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<Table | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedTableForQR, setSelectedTableForQR] = useState<Table | null>(null);
  const [generatingQR, setGeneratingQR] = useState<string | null>(null);
  const [generatingAllQR, setGeneratingAllQR] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  // `string` explícito: el hook usa sortSelected/onSort con `string[]`, y evita que el
  // literal `SortValue` de TABLES_SORT_OPTIONS estreche de más el estado.
  const [sortOption, setSortOption] = useState<string>(TABLES_SORT_OPTIONS[0].value);

  // Paginación
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 25,
    total: null,
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  /*
   * La lista de mesas, cacheada por combinación de filtros.
   *
   * ⭐ Esta pantalla tenía escrito a mano lo que la caché ya hace: un `AbortController` con su
   * efecto de limpieza, un `aborted` para no pisar el estado con una respuesta vieja, y un
   * `isAbortError` para distinguir «se canceló» de «falló». Todo eso existía porque al tipear en el
   * buscador salían varias consultas y ganaba la que contestara última, no la que el usuario estaba
   * mirando. Con los filtros dentro de la CLAVE el problema desaparece de raíz: cada combinación es
   * una entrada distinta, y la caché ignora sola las respuestas que ya no corresponden.
   *
   * `mantenerAnterior` es lo que hace que la tabla no parpadee en vacío mientras se tipea: se
   * quedan las filas anteriores hasta que llegan las nuevas.
   */
  const claveMesas = useMemo(
    () => [
      'mesas',
      branchId,
      debouncedSearchQuery.trim(),
      [...filterStatus].sort().join(','),
      sortOption,
      pagination.page,
      pagination.pageSize,
    ],
    [branchId, debouncedSearchQuery, filterStatus, sortOption, pagination.page, pagination.pageSize],
  );

  const { datos, cargando: loading, recargar } = useDatosCacheados<ListadoMesas>({
    clave: claveMesas,
    activo: !authLoading && !!branchId,
    mantenerAnterior: true,
    traer: async (señal) => {
      // Si estamos OFFLINE, usar caché SIN validación de frescura
      if (!navigator.onLine) {
        return { filas: await getCachedTables<Table>(), total: null };
      }

      if (!branchId) {
        throw new Error('No se encontró la sucursal. Por favor selecciona una sucursal.');
      }

      try {
        // Cargar desde Supabase con campos específicos y paginación
        let query = supabase
          .from('tables')
          .select('id,table_number,name,capacity,status,location,notes,branch_id', { count: 'exact' })
          .eq('branch_id', branchId)
          .abortSignal(señal);

        // Aplicar búsqueda en servidor si hay query (usar debounced)
        if (debouncedSearchQuery.trim()) {
          query = query.or(`table_number.ilike.%${debouncedSearchQuery}%,name.ilike.%${debouncedSearchQuery}%`);
        }
        if (filterStatus.length > 0) {
          query = query.in('status', filterStatus);
        }

        const ascending = sortOption.includes('asc');
        query = query
          .order('table_number', { ascending })
          .range(pagination.offset, pagination.offset + pagination.pageSize - 1);

        const { data: tablesData, error: tablesError, count } = await query;

        if (tablesError) throw tablesError;

        // SIEMPRE cargar QR codes desde la BD (no desde caché)
        const tableNumbers = (tablesData || []).map((t: Record<string, unknown>) => t.table_number);
        let qrCodesMap: Record<string, { id: string; qr_code: string }> = {};

        if (tableNumbers.length > 0) {
          // Primero intentar con is_active = true
          const { data: initialQrData, error: qrError } = await supabase
            .from('table_qr_codes')
            .select('id, qr_code, table_number, is_active')
            .eq('branch_id', branchId)
            .in('table_number', tableNumbers)
            .eq('is_active', true)
            .abortSignal(señal);

          let qrData = initialQrData;

          // Si no encuentra nada, buscar sin filtro de is_active (por si acaso)
          if (!señal.aborted && !qrError && (!qrData || qrData.length === 0)) {
            const { data: allQrData, error: allQrError } = await supabase
              .from('table_qr_codes')
              .select('id, qr_code, table_number, is_active')
              .eq('branch_id', branchId)
              .in('table_number', tableNumbers)
              .abortSignal(señal);
            if (!allQrError && allQrData) {
              qrData = allQrData;
            }
          }

          if (qrError && !isAbortError(qrError)) {
            devLog.error('❌ Error cargando QR codes:', qrError);
          }
          if (qrData && qrData.length > 0) {
            qrCodesMap = (qrData as QrCodeRow[]).reduce<Record<string, { id: string; qr_code: string }>>((acc, qr) => {
              acc[qr.table_number] = { id: qr.id, qr_code: qr.qr_code };
              return acc;
            }, {});
          }
        }

        // Procesar datos y agregar QR codes
        const freshTables: Table[] = ((tablesData || []) as TableRow[]).map((table) => {
          const qrInfo = qrCodesMap[table.table_number];
          return { ...table, qr_code: qrInfo?.qr_code ?? null, qr_id: qrInfo?.id ?? null };
        });

        // Actualizar caché solo en primera página (para modo offline)
        if (pagination.page === 1 && !debouncedSearchQuery.trim()) {
          await cacheTables(freshTables);
        }

        return { filas: freshTables, total: count };
      } catch (error: unknown) {
        if (isAbortError(error)) throw error;
        /*
         * 🪤 Se cae al disco y se avisa, las DOS cosas. Sólo lanzar dejaría al mozo con la pantalla
         * vacía teniendo las mesas guardadas; sólo devolver el disco le mostraría datos viejos sin
         * decirle que la consulta falló. El aviso va acá y no por `mensajeDeError` justamente
         * porque este camino NO termina en error: termina con datos, pero degradados.
         */
        devLog.error('❌ Error al cargar mesas:', error);
        const cached = await getCachedTables<Table>().catch(() => [] as Table[]);
        if (cached.length === 0) throw error;
        toast.error('Error al cargar mesas', getErrorMessage(error));
        return { filas: cached, total: null };
      }
    },
  });

  const tables = datos?.filas ?? SIN_MESAS;
  const totalMesas = datos?.total ?? null;

  const { setTotal } = pagination;
  useEffect(() => {
    if (totalMesas !== null) setTotal(totalMesas);
  }, [totalMesas, setTotal]);

  const loadTables = useCallback(() => { void recargar(); }, [recargar]);

  // Resetear a página 1 cuando cambie la búsqueda, el filtro de estado o el orden.
  useEffect(() => {
    pagination.goToFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo resetear cuando cambian búsqueda, filtro u orden
  }, [debouncedSearchQuery, filterStatus, sortOption]);

  const handleCreate = useCallback(() => {
    setSelectedTable(null);
    setTableFormOpen(true);
  }, []);

  const stats = useMemo(
    () => ({
      total: tables.length,
      free: tables.filter((t) => t.status === 'free').length,
      occupied: tables.filter((t) => t.status === 'occupied').length,
      reserved: tables.filter((t) => t.status === 'reserved').length,
      maintenance: tables.filter((t) => t.status === 'maintenance').length,
    }),
    [tables]
  );

  const config = useMemo(
    () =>
      getTablesListConfig(() => navigate(ROUTES.APPS_RESTAURANT_POS), {
        onBack: () => navigate(ROUTES.APPS_RESTAURANT_POS),
        onAddTable: handleCreate,
      }),
    [navigate, handleCreate]
  );

  const {
    activeView,
    selectedTabIndex,
    handleSwitchView,
    handleCancelFilter,
    handleSaveView,
    createViewWithName,
    hasUnsavedFilterChanges,
    indexFiltersTabs,
  } = useListViews({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    systemViews: TABLES_SYSTEM_VIEWS,
    searchValue: searchQuery,
    filters: { status: filterStatus },
    setSearchValue: setSearchQuery,
    setFilters: (f) => setFilterStatus(f.status ?? []),
    setMode,
    mode,
  });

  const appliedFilters = useMemo(() => {
    const items: { key: string; label: string; onRemove: () => void }[] = [];
    if (filterStatus.length > 0) {
      const labels = filterStatus.map((s) => statusLabels[s as Table['status']] ?? s).join(', ');
      items.push({ key: 'status', label: `Estado: ${labels}`, onRemove: () => setFilterStatus([]) });
    }
    return items;
  }, [filterStatus]);

  const indexFiltersFilters = useMemo(
    () => [
      {
        key: 'status',
        label: 'Estado',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Estado"
            titleHidden
            choices={[
              { label: 'Libre', value: 'free' },
              { label: 'Ocupada', value: 'occupied' },
              { label: 'Reservada', value: 'reserved' },
              { label: 'Mantenimiento', value: 'maintenance' },
            ]}
            selected={filterStatus}
            onChange={setFilterStatus}
            allowMultiple
          />
        ),
      },
    ],
    [filterStatus]
  );

  const clearFilters = useCallback(() => {
    setFilterStatus([]);
    handleSwitchView(0);
  }, [handleSwitchView]);

  const {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode,
    editingColumnPrefs,
    onColumnOrderChange,
    onToggleColumnVisibility,
  } = useStandardListFilters({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    config,
    views: {
      activeView,
      selectedTabIndex,
      handleSwitchView,
      handleCancelFilter,
      handleSaveView,
      createViewWithName,
      hasUnsavedFilterChanges,
      indexFiltersTabs,
    },
    searchValue: searchQuery,
    setSearchValue: setSearchQuery,
    queryPlaceholder: activeView ? `Buscar en ${activeView.label}` : 'Buscar por número, nombre o ubicación…',
    filters: indexFiltersFilters,
    appliedFilters,
    onClearAll: clearFilters,
    sortOptions: TABLES_SORT_OPTIONS,
    sortSelected: [sortOption],
    onSort: (v) => setSortOption(v[0] ?? sortOption),
    mode,
    setMode,
    showEditColumnsButton: true,
  });

  const listItems: TableListItem[] = useMemo(
    () =>
      tables.map((table) => ({
        id: table.id,
        table_number: table.table_number,
        name: table.name ?? null,
        capacity: table.capacity,
        location: table.location ?? null,
        status: table.status,
        status_label: statusLabels[table.status],
        status_tone: statusTones[table.status],
        qr_display: table.qr_code ? 'Generado' : 'Sin QR',
      })),
    [tables]
  );

  const contentAboveCard = useMemo(
    () => (
      <StatsSnapshotBar
        items={[
          { label: 'Total', value: stats.total },
          { label: 'Libres', value: stats.free, tone: 'success' },
          { label: 'Ocupadas', value: stats.occupied, tone: 'critical' },
          { label: 'Reservadas', value: stats.reserved, tone: 'caution' },
          { label: 'Mantenimiento', value: stats.maintenance, tone: 'subdued' },
        ]}
      />
    ),
    [stats.total, stats.free, stats.occupied, stats.reserved, stats.maintenance]
  );

  const pageTitleNode = (
    <PageBreadcrumbTitle
      icon={PointOfSaleIcon}
      title={config.title}
      onBack={config.onBack}
      backAccessibilityLabel={config.backAccessibilityLabel ?? 'Volver a Punto de venta'}
    />
  );

  const handleBulkAction = useCallback(
    (_actionKey: string, selectedIds: string[]) => {
      if (selectedIds.length === 0) return;
      // Export: podría implementarse con exportListToExcel
    },
    []
  );

  const handleEdit = (table: Table) => {
    setSelectedTable(table);
    setTableFormOpen(true);
  };

  const handleDelete = (table: Table) => {
    setTableToDelete(table);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tableToDelete) return;

    try {
      const { error } = await supabase
        .from('tables')
        .delete()
        .eq('id', tableToDelete.id);

      if (error) throw error;

      toast.success('Mesa eliminada', `Mesa ${tableToDelete.table_number} ha sido eliminada`);

      loadTables();
    } catch (error: unknown) {
      devLog.error('Error al eliminar mesa:', error);
      toast.error('Error al eliminar', getErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setTableToDelete(null);
    }
  };

  // Generar QR code para una mesa
  const handleGenerateQR = async (table: Table) => {
    if (!branchId) return;

    try {
      setGeneratingQR(table.id);

      const { data, error } = await supabase.rpc('upsert_table_qr_code', {
        p_branch_id: branchId,
        p_table_number: table.table_number,
      });

      if (error) {
        devLog.error('❌ Error en RPC upsert_table_qr_code:', error);
        throw error;
      }

      if (data && data.length > 0) {
        const qrData = data[0];
        // Construir URL completa del QR
        const baseUrl = window.location.origin;
        const qrUrl = `${baseUrl}/table/${qrData.qr_code}`;

        // El QR aparece al instante en la fila, escribiendo en la caché. Antes se parcheaba un
        // `useState`: al volver a la lista el QR recién generado no estaba.
        queryClient.setQueryData<ListadoMesas>(claveMesas, (prev) =>
          prev
            ? {
                ...prev,
                filas: prev.filas.map((t) =>
                  t.id === table.id ? { ...t, qr_code: qrData.qr_code, qr_id: qrData.id } : t,
                ),
              }
            : prev,
        );

        // Verificar que se guardó en BD haciendo una consulta inmediata
        const { data: verifyData, error: verifyError } = await supabase
          .from('table_qr_codes')
          .select('id, qr_code, table_number, is_active')
          .eq('branch_id', branchId)
          .eq('table_number', table.table_number)
          .single();
        
        if (verifyError) {
          devLog.error('⚠️ Error verificando QR guardado:', verifyError);
        }

        // Mostrar modal con QR
        setSelectedTableForQR({
          ...table,
          qr_code: qrData.qr_code,
          qr_id: qrData.id,
        });
        setQrModalOpen(true);

        toast.success('QR code generado', `QR code para ${table.table_number} generado correctamente`);
      } else {
        devLog.error('⚠️ La función upsert_table_qr_code no retornó datos');
      }
    } catch (error: unknown) {
      devLog.error('Error generando QR:', error);
      toast.error('Error al generar QR', getErrorMessage(error) || 'Intenta nuevamente');
    } finally {
      setGeneratingQR(null);
    }
  };

  // Ver QR code existente
  const handleViewQR = (table: Table) => {
    if (table.qr_code) {
      setSelectedTableForQR(table);
      setQrModalOpen(true);
    } else {
      // Si no tiene QR, generarlo
      handleGenerateQR(table);
    }
  };

  const renderTableActions = useCallback(
    (item: TableListItem) => {
      const table = tables.find((t) => t.id === item.id);
      if (!table) return null;
      return (
        <ButtonGroup>
          {table.qr_code ? (
            <>
              <Button
                icon={ViewIcon}
                variant="tertiary"
                accessibilityLabel="Ver QR"
                onClick={() => handleViewQR(table)}
              />
              <Button
                icon={RefreshIcon}
                variant="tertiary"
                accessibilityLabel="Regenerar QR"
                loading={generatingQR === table.id}
                onClick={() => handleGenerateQR(table)}
              />
            </>
          ) : (
            <Button
              icon={ViewIcon}
              variant="tertiary"
              accessibilityLabel="Generar QR"
              loading={generatingQR === table.id}
              onClick={() => handleViewQR(table)}
            />
          )}
          <Button
            icon={EditIcon}
            variant="tertiary"
            accessibilityLabel="Editar"
            onClick={() => handleEdit(table)}
          />
          <Button
            icon={DeleteIcon}
            variant="tertiary"
            tone="critical"
            accessibilityLabel="Eliminar"
            onClick={() => handleDelete(table)}
          />
        </ButtonGroup>
      );
    },
    [tables, generatingQR]
  );

  // Generar QR codes para todas las mesas
  const handleGenerateAllQR = async () => {
    if (!branchId || tables.length === 0 || generatingAllQR) return;

    try {
      setGeneratingAllQR(true);
      let successCount = 0;
      let errorCount = 0;

      for (const table of tables) {
        try {
          const { error } = await supabase.rpc('upsert_table_qr_code', {
            p_branch_id: branchId,
            p_table_number: table.table_number,
          });

          if (error) {
            devLog.error(`Error generando QR para mesa ${table.table_number}:`, error);
            errorCount++;
          } else {
            successCount++;
          }
        } catch (err) {
          devLog.error(`Error generando QR para mesa ${table.table_number}:`, err);
          errorCount++;
        }
      }

      // Recargar mesas para mostrar los QR generados
      await loadTables();

      if (successCount > 0) {
        toast.success('QR codes generados', `${successCount} QR code(s) generado(s) correctamente${errorCount > 0 ? `. ${errorCount} error(es).` : ''}`);
      } else {
        toast.error('Error al generar QR codes', 'No se pudo generar ningún QR code');
      }
    } catch (error: unknown) {
      devLog.error('Error generando todos los QR:', error);
      toast.error('Error al generar QR codes', getErrorMessage(error) || 'Intenta nuevamente');
    } finally {
      setGeneratingAllQR(false);
    }
  };

  // Copiar URL del QR al portapapeles
  const handleCopyQRUrl = (qrCode: string) => {
    const baseUrl = window.location.origin;
    const qrUrl = `${baseUrl}/table/${qrCode}`;
    
    navigator.clipboard.writeText(qrUrl).then(() => {
      toast.success('URL copiada', 'La URL del QR code ha sido copiada al portapapeles');
    }).catch((err) => {
      devLog.error('Error copiando URL:', err);
      toast.error('Error al copiar URL');
    });
  };

  if (!branchId) {
    return (
      <Page
        title={
          <PageBreadcrumbTitle
            icon={PointOfSaleIcon}
            title="Gestión de mesas"
            onBack={() => navigate(ROUTES.APPS_RESTAURANT_POS)}
            backAccessibilityLabel="Volver a Punto de venta"
          />
        }
      >
        <Card>
          <EmptyState
            heading="No hay sucursal seleccionada"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Por favor selecciona una sucursal para gestionar las mesas.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <>
      <ListView<TableListItem>
        config={config}
        items={listItems}
        loading={loading || authLoading}
        loadingMessage="Cargando mesas…"
        pageTitle={pageTitleNode}
        contentAboveCard={contentAboveCard}
        contentAboveCardInCard={false}
        contentBefore={filterControl}
        contentBeforeInsideCard
      secondaryActions={[
        {
          // El wrapper ListView/AppPage no expone loading/disabled; el label refleja
          // el progreso y handleGenerateAllQR ya evita reentrada y lista vacía.
          content: generatingAllQR ? 'Generando...' : 'Generar todos los QR',
          onAction: handleGenerateAllQR,
        },
      ]}
        onRowEdit={(item) => {
          const t = tables.find((x) => x.id === item.id);
          if (t) handleEdit(t);
        }}
        onRowDelete={(item) => {
          const t = tables.find((x) => x.id === item.id);
          if (t) handleDelete(t);
        }}
        onRenderActions={renderTableActions}
        pagination={buildListViewPagination(pagination)}
        columnOrder={tableColumnOrder}
        hiddenColumnKeys={tableHiddenKeys}
        editColumnsMode={editColumnsMode}
        columnOrderForEdit={editingColumnPrefs?.columnOrder?.length ? editingColumnPrefs.columnOrder : tableColumnOrder}
        onColumnOrderChange={onColumnOrderChange}
        onToggleColumnVisibility={onToggleColumnVisibility}
        onBulkAction={handleBulkAction}
                  selectable={false}
      />

          {/* Table Form Modal */}
          <TableFormModal
            open={tableFormOpen}
            onClose={() => setTableFormOpen(false)}
            table={selectedTable}
            onSuccess={loadTables}
          />

          {/* Delete Dialog */}
          <Modal
            open={deleteDialogOpen}
            onClose={() => {
              if (!deleteDialogOpen) return;
              setDeleteDialogOpen(false);
              setTableToDelete(null);
            }}
            title="Eliminar mesa"
            primaryAction={{
              content: 'Eliminar',
              tone: 'critical',
              onAction: handleDeleteConfirm,
            }}
            secondaryActions={[
              {
                content: 'Cancelar',
                onAction: () => {
                  setDeleteDialogOpen(false);
                  setTableToDelete(null);
                },
              },
            ]}
          >
            <Modal.Section>
              <Text as="p">
                Esta acción no se puede deshacer. Se eliminará la mesa{' '}
                <Text as="span" fontWeight="semibold">
                  {tableToDelete?.table_number}
                </Text>{' '}
                permanentemente.
              </Text>
            </Modal.Section>
          </Modal>

          {/* QR Code Modal */}
          {selectedTableForQR && (
            <Modal
              open={qrModalOpen}
              onClose={() => {
                setQrModalOpen(false);
                setSelectedTableForQR(null);
              }}
              title={`QR Code - ${selectedTableForQR.name || `Mesa ${selectedTableForQR.table_number}`}`}
              primaryAction={{
                content: 'Copiar URL',
                onAction: () => selectedTableForQR.qr_code && handleCopyQRUrl(selectedTableForQR.qr_code),
              }}
              secondaryActions={[
                {
                  content: 'Cerrar',
                  onAction: () => {
                    setQrModalOpen(false);
                    setSelectedTableForQR(null);
                  },
                },
              ]}
            >
              <Modal.Section>
                <BlockStack gap="500">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Los clientes pueden escanear este código QR para hacer su pedido directamente desde su mesa.
                  </Text>

                  {selectedTableForQR.qr_code && (
                    <>
                      <Box padding="600" background="bg-surface-secondary" borderRadius="200">
                        <InlineStack align="center" blockAlign="center">
                          <div id="qr-code-container">
                            <QRCodeSVG
                              value={`${window.location.origin}/table/${selectedTableForQR.qr_code}`}
                              size={256}
                              level="M"
                              includeMargin={true}
                            />
                          </div>
                        </InlineStack>
                      </Box>

                      <InlineStack align="center" blockAlign="center" gap="300">
                        <Button
                          onClick={() => {
                            // Descargar QR como imagen
                            const qrContainer = document.getElementById('qr-code-container');
                            if (qrContainer) {
                              const svg = qrContainer.querySelector('svg');
                              if (svg) {
                                const svgData = new XMLSerializer().serializeToString(svg);
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                const img = new Image();
                                
                                img.onload = () => {
                                  canvas.width = img.width;
                                  canvas.height = img.height;
                                  ctx?.drawImage(img, 0, 0);
                                  canvas.toBlob((blob) => {
                                    if (blob) {
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `QR-Mesa-${selectedTableForQR.table_number}.png`;
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      URL.revokeObjectURL(url);
                                      toast.success('QR descargado', 'El código QR se ha descargado correctamente');
                                    }
                                  });
                                };
                                
                                img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                              }
                            }
                          }}
                        >
                          Descargar QR
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            // Copiar URL al portapapeles
                            const url = `${window.location.origin}/table/${selectedTableForQR.qr_code}`;
                            navigator.clipboard.writeText(url);
                            toast.success('URL copiada', 'La URL del QR se ha copiado al portapapeles');
                          }}
                        >
                          Copiar URL
                        </Button>
                      </InlineStack>

                      <Card>
                        <Box padding="400">
                          <BlockStack gap="200">
                            <Text as="p" variant="bodySm" fontWeight="semibold">
                              URL del QR Code:
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued" breakWord>
                              {`${window.location.origin}/table/${selectedTableForQR.qr_code}`}
                            </Text>
                          </BlockStack>
                        </Box>
                      </Card>

                      <Banner status="info">
                        <BlockStack gap="200">
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            Instrucciones:
                          </Text>
                          <Text as="p" variant="bodySm">
                            1. Imprime este QR code y colócalo en la mesa {selectedTableForQR.table_number}
                            <br />
                            2. Los clientes escanearán el código con su celular
                            <br />
                            3. Se abrirá automáticamente el menú para hacer su pedido
                          </Text>
                        </BlockStack>
                      </Banner>
                    </>
                  )}
                </BlockStack>
              </Modal.Section>
            </Modal>
          )}
    </>
  );
}
